<template>
    <Head title="Notifications" />

    <div class="px-4 md:px-8" data-page="notifications">
        <div
            class="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div>
                <h1 class="text-2xl font-bold leading-8 text-gray-900 dark:text-gray-100">
                    Notifications
                </h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    A complete history of your My10x notifications.
                </p>
            </div>

            <button
                @click="markAllAsRead"
                :disabled="!hasUnreadNotifications || markingRead"
                class="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm dark:shadow-slate-900/50 transition hover:bg-gray-50 dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <i
                    class="fas"
                    :class="markingRead ? 'fa-spinner fa-spin' : 'fa-check'"
                ></i>
                Mark all as read
            </button>
        </div>

        <div
            class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50"
        >
            <div v-if="loading" class="py-16">
                <LoadingInline :loading="true" />
            </div>

            <div
                v-else-if="notifications.length === 0"
                class="flex flex-col items-center justify-center px-6 py-16 text-center"
            >
                <div
                    class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-300"
                >
                    <i class="far fa-bell text-xl"></i>
                </div>
                <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
                    No notifications yet
                </h2>
                <p class="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                    New account, card, alert, and system notifications will
                    appear here.
                </p>
            </div>

            <div v-else class="divide-y divide-gray-200 dark:divide-slate-700">
                <div
                    v-for="notification in notifications"
                    :key="notification.id"
                    class="flex items-start gap-4 px-5 py-4 transition sm:px-6"
                    :class="{ 'bg-blue-50/40 dark:bg-blue-900/20': !notification.read_at }"
                >
                    <span
                        class="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                    >
                        <span
                            v-if="!notification.read_at"
                            class="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500"
                        ></span>
                        <i class="fas" :class="iconFor(notification)"></i>
                    </span>

                    <div class="min-w-0 flex-1">
                        <div
                            class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
                        >
                            <div class="min-w-0">
                                <div
                                    class="font-medium text-sm leading-5 text-slate-800 dark:text-slate-100"
                                >
                                    {{ notification.title || 'Notification' }}
                                </div>
                                <div
                                    v-if="notification.message"
                                    class="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300"
                                >
                                    {{ notification.message }}
                                </div>
                            </div>

                            <span
                                v-if="!notification.read_at"
                                class="inline-flex shrink-0 items-center rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-200"
                            >
                                New
                            </span>
                        </div>

                        <div
                            v-if="notification.programs && notification.programs.length"
                            class="mt-2 flex flex-wrap items-center gap-2"
                        >
                            <UiTooltip
                                v-for="program in notification.programs"
                                :key="programName(program)"
                                :text="programName(program)"
                                placement="top"
                            >
                                <template #trigger>
                                    <span
                                        class="inline-flex h-8 items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2"
                                    >
                                        <img
                                            v-if="programImage(program)"
                                            :src="programImage(program)"
                                            :alt="programName(program)"
                                            class="h-5 max-w-[80px] object-contain"
                                        />
                                        <span
                                            v-else
                                            class="text-xs font-medium text-slate-700 dark:text-slate-200"
                                        >
                                            {{ programName(program) }}
                                        </span>
                                    </span>
                                </template>
                            </UiTooltip>
                        </div>

                        <div
                            class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-gray-500 dark:text-gray-400"
                        >
                            <span>
                                {{ getTimeAgo(notification.created_at) }}
                            </span>
                            <span
                                v-if="notification.category"
                                class="inline-flex items-center rounded bg-gray-100 dark:bg-slate-700 px-2 py-0.5 font-medium text-gray-600 dark:text-gray-300"
                            >
                                {{ categoryLabel(notification.category) }}
                            </span>
                        </div>

                        <div class="mt-3" v-if="isConnectionIssue(notification)">
                            <ConnectionResolver
                                :notification="notification"
                                @resolved="markNotificationRead(notification)"
                            />
                        </div>

                        <div
                            v-else-if="isWalletInvitation(notification)"
                            class="mt-3 flex items-center gap-2"
                        >
                            <button
                                type="button"
                                @click="acceptInvitation(notification)"
                                :disabled="invitationLoading"
                                class="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-slate-100 shadow-sm transition hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i class="far fa-check text-[11px]"></i>
                                Accept
                            </button>
                            <button
                                type="button"
                                @click="openRejectModal(notification)"
                                :disabled="invitationLoading"
                                class="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-slate-100 shadow-sm transition hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i class="far fa-times text-[11px]"></i>
                                Reject
                            </button>
                        </div>

                        <div v-else-if="notification.route" class="mt-3">
                            <button
                                type="button"
                                @click="handleCtaClick(notification)"
                                class="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-slate-100 shadow-sm transition hover:bg-slate-50 dark:hover:bg-slate-700"
                            >
                                {{ notification.cta_label || 'View details' }}
                                <i class="far fa-arrow-right text-[10px]"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="meta && meta.last_page > 1"
                class="flex flex-col gap-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            >
                <div class="text-sm text-gray-600 dark:text-gray-400">
                    Page {{ meta.current_page }} of {{ meta.last_page }}
                    <span class="text-gray-400">({{ meta.total }} total)</span>
                </div>

                <div class="flex items-center gap-2">
                    <button
                        @click="loadPage(meta.current_page - 1)"
                        :disabled="meta.current_page <= 1"
                        class="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <i class="far fa-arrow-left"></i>
                        Previous
                    </button>
                    <button
                        @click="loadPage(meta.current_page + 1)"
                        :disabled="meta.current_page >= meta.last_page"
                        class="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Next
                        <i class="far fa-arrow-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Reject invitation confirmation modal -->
        <div
            v-if="showRejectModal"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            @click.self="closeRejectModal"
        >
            <div class="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 p-6 shadow-xl">
                <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
                    Reject invitation
                </h3>
                <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    You are about to reject
                    <span class="font-medium">{{ rejectingSenderName }}</span
                    >'s invitation to share their wallet. This cannot be undone.
                </p>
                <div class="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        @click="closeRejectModal"
                        class="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-slate-100 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        @click="confirmRejectInvitation"
                        :disabled="invitationLoading"
                        class="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:opacity-50"
                    >
                        {{
                            invitationLoading
                                ? 'Rejecting...'
                                : 'Reject invitation'
                        }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import ConnectionResolver from '@/components/notifications/connection-resolver.vue';
import { useDecisionsModalStore } from '@/store/decisions-modal';
import LoadingInline from '@/components/ui/loading-inline.vue';

const decisionsModalStore = useDecisionsModalStore();

const notifications = ref([]);
const meta = ref(null);
const loading = ref(false);
const markingRead = ref(false);
const currentPage = ref(1);

const hasUnreadNotifications = computed(() => {
    return notifications.value.some((notification) => !notification.read_at);
});

const fetchNotifications = async (page = 1) => {
    loading.value = true;

    try {
        const response = await ApiService.apiCall(
            `/notifications/paginated?page=${page}`,
            'get'
        );
        notifications.value = response.data.data || [];
        meta.value = response.data.meta || null;
        currentPage.value = page;

        // Sync the URL so browser back from a notification CTA returns here.
        const url = new URL(window.location.href);
        if (page > 1) {
            url.searchParams.set('page', String(page));
        } else {
            url.searchParams.delete('page');
        }
        window.history.replaceState({}, '', url.toString());
    } catch (error) {
        console.error('Error fetching notifications:', error);
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Failed to load notifications.',
            },
            4000
        );
    } finally {
        loading.value = false;
    }
};

const loadPage = (page) => {
    if (page >= 1 && meta.value && page <= meta.value.last_page) {
        fetchNotifications(page);
    }
};

const markAllAsRead = async () => {
    markingRead.value = true;

    try {
        await ApiService.apiCall('/notifications/mark-all-read', 'post');

        notifications.value.forEach((notification) => {
            notification.read_at = new Date().toISOString();
        });

        notify(
            {
                group: 'success',
                title: 'Success',
                text: 'All notifications marked as read.',
            },
            4000
        );
    } catch (error) {
        console.error('Error marking all as read:', error);
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Failed to mark notifications as read.',
            },
            4000
        );
    } finally {
        markingRead.value = false;
    }
};

const markNotificationRead = (notification) => {
    if (!notification || notification.read_at) {
        return;
    }

    notification.read_at = new Date().toISOString();

    ApiService.apiCall(
        `/notifications/${notification.id}/mark-read`,
        'post'
    ).catch(() => {
        notification.read_at = null;
    });
};

// Click-tracking redirects (e.g. /go/{campaignKey}) and absolute URLs
// need a full-page navigation so the server-side 302 from
// CampaignRedirectController is followed by the browser. Inertia's
// router.visit() uses XHR and can't follow cross-origin redirects.
const needsFullPageNav = (route) =>
    !!route && (/^\/go\//.test(route) || /^https?:\/\//i.test(route));

const handleCtaClick = (notification) => {
    markNotificationRead(notification);

    if (notification.modal_type) {
        decisionsModalStore.openModal(notification.modal_type);
        return;
    }

    if (!notification.route) {
        return;
    }

    if (needsFullPageNav(notification.route)) {
        window.location.href = notification.route;
        return;
    }

    router.visit(notification.route);
};

const navigateTo = (route) => {
    if (!route) {
        return;
    }

    if (needsFullPageNav(route)) {
        window.location.href = route;
        return;
    }

    router.visit(route);
};

const iconFor = (notification) => {
    if (notification.notification_type?.startsWith('redemption_import')) {
        return 'fa-envelope-open-text';
    }

    const icons = {
        account: 'fa-university',
        alert: 'fa-bell',
        benefits: 'fa-gift',
        card: 'fa-credit-card',
        changelog: 'fa-star',
        companion_pass: 'fa-plane',
        credit_score: 'fa-chart-line',
        rewards: 'fa-coins',
        security: 'fa-shield-alt',
        system: 'fa-cog',
        wallet: 'fa-wallet',
    };

    return icons[notification.category] || 'fa-bell';
};

const programName = (program) =>
    typeof program === 'string' ? program : program?.name || '';

const programImage = (program) =>
    typeof program === 'string' ? null : program?.image || null;

const categoryLabel = (category) => {
    return category
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const isConnectionIssue = (notification) => {
    return notification?.category === 'account' && !!notification?.member_guid;
};

const isWalletInvitation = (notification) => {
    return !!notification?.invite_guid;
};

const showRejectModal = ref(false);
const rejectingNotification = ref(null);
const invitationLoading = ref(false);

const rejectingSenderName = computed(() => {
    return rejectingNotification.value?.sender_name || 'this user';
});

const acceptInvitation = (notification) => {
    if (!notification?.invite_guid || invitationLoading.value) return;

    invitationLoading.value = true;

    router.post(
        `/accept-wallet-invite/${notification.invite_guid}`,
        { user_exists: 'true' },
        {
            onSuccess: (page) => {
                markNotificationRead(notification);
                fetchNotifications(currentPage.value);
                notify(
                    {
                        group: 'success',
                        title: 'Invitation accepted',
                        text:
                            page.props.success ||
                            `Wallet invitation from ${notification.sender_name || 'sender'} accepted.`,
                    },
                    4000
                );
            },
            onError: () => {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to accept invitation.',
                    },
                    4000
                );
            },
            onFinish: () => {
                invitationLoading.value = false;
            },
        }
    );
};

const openRejectModal = (notification) => {
    rejectingNotification.value = notification;
    showRejectModal.value = true;
};

const closeRejectModal = () => {
    showRejectModal.value = false;
    rejectingNotification.value = null;
};

const confirmRejectInvitation = () => {
    const notification = rejectingNotification.value;
    if (!notification?.invite_guid || invitationLoading.value) return;

    invitationLoading.value = true;

    router.delete(`/settings/invitations/${notification.invite_guid}/delete`, {
        onSuccess: (page) => {
            markNotificationRead(notification);
            fetchNotifications(currentPage.value);
            notify(
                {
                    group: 'success',
                    title: 'Invitation rejected',
                    text:
                        page.props.success || 'Invitation rejected successfully.',
                },
                4000
            );
        },
        onError: () => {
            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: 'Failed to reject invitation.',
                },
                4000
            );
        },
        onFinish: () => {
            invitationLoading.value = false;
            closeRejectModal();
        },
    });
};

const getTimeAgo = (dateString) => {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const seconds = Math.max(0, Math.floor((new Date() - date) / 1000));

    if (seconds < 60) return `${seconds} sec ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

onMounted(() => {
    const params = new URLSearchParams(window.location.search);
    const initialPage = parseInt(params.get('page'), 10) || 1;
    fetchNotifications(initialPage);
});
</script>
